'use client';

import { useLanguage } from '@/context/LanguageContext';
import { ArrowLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Reveal from '@/components/Reveal';
import type { BlogPost } from '@/i18n/blog-data';
import type { Locale } from '@/i18n/config';

function markdownToHtml(md: string): string {
  const blocks = md.split(/\n\n+/);
  let html = '';
  let inList = false;

  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;

    // Bold
    const withBold = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    if (withBold.startsWith('## ')) {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<h2>${withBold.slice(3)}</h2>`;
    } else if (withBold.startsWith('### ')) {
      if (inList) { html += '</ul>'; inList = false; }
      html += `<h3>${withBold.slice(4)}</h3>`;
    } else if (withBold.startsWith('- ')) {
      if (!inList) { html += '<ul>'; inList = true; }
      const items = withBold.split('\n').filter(l => l.startsWith('- '));
      for (const item of items) {
        html += `<li>${item.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</li>`;
      }
    } else {
      if (inList) { html += '</ul>'; inList = false; }
      // Join lines within the same block into one paragraph
      const lines = withBold.split('\n').join(' ');
      html += `<p>${lines}</p>`;
    }
  }

  if (inList) html += '</ul>';
  return html;
}

export default function BlogPostClient({
  post,
  locale,
}: {
  post: BlogPost;
  locale: Locale;
}) {
  const { lang, t } = useLanguage();
  const content = post[lang];

  return (
    <>
      <Navbar />
      <main className="pt-24 pb-20 bg-dark-950 min-h-screen">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <a
              href={`/${lang}/blog`}
              className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-electric transition-colors duration-300 mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('Retour au blog', 'Back to blog')}
            </a>

            <time
              dateTime={post.date}
              className="block text-sm text-white/30 mb-4"
            >
              {new Date(post.date).toLocaleDateString(
                lang === 'fr' ? 'fr-FR' : 'en-US',
                { year: 'numeric', month: 'long', day: 'numeric' }
              )}
            </time>

            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-8">
              {content.title}
            </h1>
          </Reveal>

          <Reveal delay={0.1}>
            <div
              className="prose prose-invert prose-lg max-w-none
                prose-headings:font-display prose-headings:font-bold prose-headings:text-white
                prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-5 prose-h2:border-b prose-h2:border-white/[0.06] prose-h2:pb-3
                prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-white/90
                prose-p:text-white/55 prose-p:leading-[1.8] prose-p:mb-5
                prose-li:text-white/55 prose-li:leading-[1.8]
                prose-ul:my-4 prose-ul:space-y-1
                prose-strong:text-white prose-strong:font-semibold
                prose-a:text-electric prose-a:no-underline hover:prose-a:underline"
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(content.content),
              }}
            />
          </Reveal>

          {/* CTA */}
          <Reveal delay={0.2}>
            <div className="mt-16 rounded-2xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm p-8 text-center">
              <h2 className="text-2xl font-bold text-white mb-3">
                {t('Besoin d\u2019accompagnement ?', 'Need support?')}
              </h2>
              <p className="text-white/40 mb-6">
                {t(
                  'Parlons de vos enjeux IT. Premier échange gratuit et sans engagement.',
                  'Let\u2019s discuss your IT challenges. First consultation free, no commitment.'
                )}
              </p>
              <a
                href={`/${lang}#contact`}
                className="inline-flex items-center px-6 py-3 text-sm font-semibold text-white bg-electric rounded-xl hover:bg-electric-bright transition-all duration-300 shadow-[0_0_20px_rgba(79,142,255,0.25)]"
              >
                {t('Nous contacter', 'Contact us')}
              </a>
            </div>
          </Reveal>
        </article>
      </main>
      <Footer />
    </>
  );
}
